import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { EmptyTableState } from '@/modules/common/components/EmptyTableState';
import { TableFilter } from '@/modules/common/components/TableFilter';
import {
  formatReviewCalls,
  showReviewAction,
} from '@/modules/common/lib/application-review';
import { getDefaultMRTOptions } from '@/modules/common/lib/react-material-table';
import { formatConstant } from '@/modules/common/lib/string';
import {
  ApplicationStage,
  ApplicationTerm,
  ApplicationValidationType,
  IApplicationWithOrderAndCourse,
} from '@/types/application';
import { UserRole } from '@/types/user';
import { Close, Search, Visibility } from '@mui/icons-material';
import {
  Button,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { enqueueSnackbar } from 'notistack';
import pluralize from 'pluralize';
import { useState } from 'react';
import { useReviewApplicationMutation } from '../../hooks/useApplications';
import { getListApplicationsTableColumns } from './ListApplicationsTable.constants';
import { ButtonGroupNavigator } from './components/ButtonGroupNavigator';
import { ReviewModal } from './components/ReviewModal/ReviewModal';
import { ReviewModalState } from './components/ReviewModal/ReviewModal.types';

type ListApplicationsTableProps = {
  tableOptions: Omit<
    MRT_TableOptions<IApplicationWithOrderAndCourse>,
    'columns'
  >;
  countsByStage?: Record<ApplicationStage, number>;
  onReviewApplications: () => Promise<void>;
};

const defaultMRTOptions =
  getDefaultMRTOptions<IApplicationWithOrderAndCourse>();

const applicationTermFilterOptions = [
  {
    label: 'Free',
    value: ApplicationTerm.Free,
  },
  {
    label: 'Upfront',
    value: ApplicationTerm.Upfront,
  },
  {
    label: 'FixPay',
    value: ApplicationTerm.FixPay,
  },
  {
    label: 'ISA',
    value: ApplicationTerm.Isa,
  },
];

export function ListApplicationsTable({
  tableOptions,
  countsByStage,
  onReviewApplications,
}: ListApplicationsTableProps) {
  const queryClient = useQueryClient();
  const { currentUser: { roles: userRoles = [] } = {} } =
    useCurrentUserContext() ?? {};
  const { mutateAsync: reviewApplication } =
    useReviewApplicationMutation(undefined);

  const [isReviewModalOpen, setReviewModalOpen] = useState(false);
  const [applicationsToReview, setApplicationsToReview] = useState<
    IApplicationWithOrderAndCourse[]
  >([]);

  const columns = getListApplicationsTableColumns({
    onApplicationReview: (application: IApplicationWithOrderAndCourse) => {
      setApplicationsToReview([application]);
      setReviewModalOpen(true);
    },
    userRoles,
  });

  // TODO: move this to the parent component
  const reviewSelectedApplications = async (
    reviews: Partial<ReviewModalState>,
  ) => {
    if (!applicationsToReview.length) {
      return;
    }

    try {
      setReviewModalOpen(false);

      const reviewCalls = formatReviewCalls(
        applicationsToReview,
        reviews,
        userRoles as UserRole[],
      );

      // @ts-expect-error TODO: fix this
      await Promise.all(reviewCalls.map(call => reviewApplication(call)));

      enqueueSnackbar(
        `Application${applicationsToReview.length > 1 ? 's' : ''} reviewed successfully`,
        {
          variant: 'success',
        },
      );

      table.resetRowSelection();
      await onReviewApplications();
    } catch {
      enqueueSnackbar(
        `Failed to review application${applicationsToReview.length > 1 ? 's' : ''}`,
        {
          variant: 'error',
        },
      );
    }
  };

  const handleReviewSubmit = async (
    submittedReview?: Partial<ReviewModalState>,
  ) => {
    if (submittedReview) {
      try {
        await reviewSelectedApplications(submittedReview as ReviewModalState);
      } catch {
        enqueueSnackbar('Failed to review applications', {
          variant: 'error',
        });
      }
    } else {
      setApplicationsToReview([]);
      table.resetRowSelection();
    }
    setReviewModalOpen(false);
  };

  const table = useMaterialReactTable<IApplicationWithOrderAndCourse>({
    ...defaultMRTOptions,
    columns,
    muiTableHeadProps: {
      sx: {
        display: tableOptions.data.length ? undefined : 'none',
      },
    },
    enableRowSelection: row => {
      const isEmployer = userRoles.includes(UserRole.Employer);

      const canReview = showReviewAction(row.original, userRoles);

      return (
        row.original.applicationStage === ApplicationStage.ReviewRequired &&
        isEmployer &&
        canReview
      );
    },
    enableMultiRowSelection: true,
    enableTopToolbar: true,
    renderToolbarInternalActions: () => {
      return <></>;
    },
    renderTopToolbar: function TopBar({ table }) {
      const { currentUser } = useCurrentUserContext() ?? {};
      const { columnFilters, globalFilter } = table.getState();
      const isLoadingCounts = queryClient.isFetching({
        queryKey: [{ tag: 'counts' }],
        exact: false,
      });

      const numberOfRows = table.getRowCount();
      const selectedRows = table.getSelectedRowModel().rows;

      const applicationTermFilter = columnFilters.find(
        filter => filter.id === 'applicationTerm',
      );

      if (!numberOfRows && !columnFilters.length) {
        return <div className="empty-hidden"></div>;
      }

      const showClearFilters =
        columnFilters.filter(filter => filter.id !== 'applicationStage')
          .length > 0 || !!globalFilter;

      return (
        <>
          <ButtonGroupNavigator
            isLoading={isLoadingCounts > 0}
            items={Object.values(ApplicationStage)}
            countsByItem={countsByStage}
            value={
              table
                .getState()
                .columnFilters.find(filter => filter.id === 'applicationStage')
                ?.value as ApplicationStage
            }
            onChange={async value => {
              table.setColumnFilters(prev => [
                ...prev.filter(({ id }) => id !== 'applicationStage'),
                {
                  id: 'applicationStage',
                  value,
                },
              ]);
            }}
          />

          <nav className="flex gap-12 px-4 py-3">
            <div className="flex gap-12 items-center">
              <TableFilter
                label="Type"
                id="type-select"
                value={applicationTermFilter?.value ?? ''}
                onChange={e => {
                  table.setColumnFilters(prev => [
                    ...prev.filter(({ id }) => id !== 'applicationTerm'),
                    {
                      id: 'applicationTerm',
                      value: e.target.value,
                    },
                  ]);
                }}
                displayEmpty
                renderValue={selected => {
                  if (!selected) return 'All';
                  if (selected === 'LEARNING_BUDGET') return 'Learning Budget';
                  return formatConstant(selected as ApplicationTerm);
                }}
              >
                <MenuItem key={'all'} value={''}>
                  All
                </MenuItem>
                <MenuItem key="LEARNING_BUDGET" value="LEARNING_BUDGET">
                  <ListItemText>Learning Budget</ListItemText>
                </MenuItem>
                {applicationTermFilterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <ListItemText>{option.label}</ListItemText>
                  </MenuItem>
                ))}
              </TableFilter>
            </div>
            {showClearFilters && (
              <div className="flex items-center">
                <Button
                  variant="text"
                  className="text-xs font-medium text-mydra-purple"
                  onClick={() => {
                    table.setColumnFilters(prev =>
                      prev.filter(({ id }) => id === 'applicationStage'),
                    );
                    table.resetGlobalFilter();
                  }}
                >
                  Clear
                </Button>
              </div>
            )}

            <TextField
              id="applicationSearch"
              size="small"
              slotProps={{
                input: {
                  endAdornment: <Search className="ml-3" />,
                  className: 'focus:outline-none',
                  autoFocus: !!globalFilter,
                },
              }}
              className="ml-auto"
              placeholder={
                [UserRole.TaxBenefitsProvider, UserRole.Employer].some(role =>
                  currentUser?.roles.includes(role),
                )
                  ? 'Search by employee...'
                  : 'Search by student...'
              }
              value={globalFilter}
              onChange={e => {
                table.setGlobalFilter(e.target.value);
              }}
            />
          </nav>

          {selectedRows.length > 0 && (
            <div className="flex gap-3 items-center p-4 w-full text-xs font-medium rounded-md bg-gray-background">
              <Typography variant="caption" className="font-semibold">
                {pluralize('item', selectedRows.length, true)} selected
              </Typography>
              <Button
                onClick={() => table.resetRowSelection()}
                variant="text"
                color="info"
                startIcon={<Close />}
                className="font-semibold"
              >
                Deselect all
              </Button>

              <Button
                onClick={() => {
                  setApplicationsToReview(
                    selectedRows.map(row => row.original),
                  );
                  setReviewModalOpen(true);
                }}
                variant="text"
                className="ml-auto font-semibold"
                startIcon={<Visibility />}
              >
                Review all
              </Button>
            </div>
          )}
        </>
      );
    },
    renderEmptyRowsFallback: ({ table }) => {
      const { columnFilters, globalFilter } = table.getState();

      const hasFilters = columnFilters.length > 0 || !!globalFilter;

      return (
        <EmptyTableState
          hasFilters={hasFilters}
          emptyStateImage="/images/no-applications.png"
          emptyStateTitle="You don't have any application yet"
          emptyStateMessage="No applications received yet. Check back later or explore other options."
        />
      );
    },
    enablePagination: !!tableOptions?.rowCount,
    ...tableOptions,
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <ReviewModal
        applicationsToReview={applicationsToReview}
        userRoles={userRoles as UserRole[]}
        onClose={handleReviewSubmit}
        open={isReviewModalOpen}
        initialReview={{
          [ApplicationValidationType.Scoring]: false,
          [ApplicationValidationType.LearningBudget]: false,
          [ApplicationValidationType.FlexibleRemuneration]: false,
          [ApplicationValidationType.Skills]: false,
          [ApplicationValidationType.Capacity]: false,
          [ApplicationValidationType.AdmissionProcess]: false,
        }}
      />
    </>
  );
}
